import { lazy, Suspense, useEffect, useState } from 'react';
import { LabAnalysis, LabSelector } from './LabExplorer';
import PaxosExperience from './PaxosExperience';
import labAnalyses from './labAnalyses';
import { getLab } from '../dslabs/protocols/registry';
import { guidedLink } from '../dslabs/ui/guidedLinks';
import { parseReplayFromLocation } from '../dslabs/ui/replayLinks';
import SimulatorErrorBoundary from '../dslabs/ui/SimulatorErrorBoundary';
import { applyPageSeo, resetSeo } from '../../seo';
import '../../css/PaxosPage.css';
import '../dslabs/ui/Simulator.css';

const SandboxView = lazy(() => import('../dslabs/ui/SandboxView'));
const SearchView = lazy(() => import('../dslabs/ui/SearchView'));

const protocolAuditUrl = 'https://github.com/adityapatel-sudo/adityapatel.net_react/blob/codex/paxos-visualizer/docs/dslabs/PROTOCOL-AUDIT.md';

const MODES = [
    { id: 'guided', label: 'Guided' },
    { id: 'sandbox', label: 'Sandbox' },
    { id: 'search', label: 'Search' },
];

export default function PaxosPage() {
    const [selectedLab, setSelectedLab] = useState(3);
    const selectedAnalysis = labAnalyses.find(({ id }) => id === selectedLab);
    const isCoursework = window.location.pathname === '/coursework/distributed-systems';
    const [mode, setMode] = useState(() => {
        const hash = window.location.hash.replace('#', '');
        if (MODES.some((m) => m.id === hash)) return hash;
        if (window.location.pathname === '/coursework/distributed-systems' && parseReplayFromLocation(window.location)) {
            return 'sandbox';
        }
        return 'guided';
    });
    const [sandboxLoad, setSandboxLoad] = useState(() => {
        if (window.location.pathname !== '/coursework/distributed-systems') return null;
        const shared = parseReplayFromLocation(window.location);
        return shared ? { ...shared, nonce: 0 } : null;
    });

    const selectMode = (id) => {
        if (id !== 'sandbox') setSandboxLoad(null);
        setMode(id);
        window.location.hash = id;
    };

    const loadPathInSandbox = (labId, seed, decisions) => {
        setSandboxLoad({ labId, seed, decisions, nonce: Date.now() });
        setMode('sandbox');
        window.location.hash = 'sandbox';
    };

    const tryGuidedInSandbox = (guidedLabId, guidedScenarioId) => {
        const link = guidedLink(guidedLabId, guidedScenarioId);
        if (!link) return;
        const lab = getLab(link.labId);
        const builder = lab.scenarios[link.scenario];
        if (!builder) return;
        const { seed, decisions } = builder();
        loadPathInSandbox(link.labId, seed, decisions);
    };

    useEffect(() => {
        const previousTitle = document.title;
        document.title = window.location.pathname === '/coursework/distributed-systems'
            ? 'Distributed Systems Labs · Aditya Patel'
            : 'Paxos Visualizer · Aditya Patel';
        applyPageSeo('distributed-systems');
        return () => { document.title = previousTitle; resetSeo(); };
    }, []);

    return (
        <div className="paxos-page">
            <header className="paxos-hero">
                <nav className="paxos-nav" aria-label="Paxos page navigation">
                    <a href="/">Aditya Patel</a><span>DSLabs / CS 50500</span>
                </nav>
                <div className="paxos-hero-copy">
                    <p className="paxos-kicker"><span /> Distributed systems coursework</p>
                    <h1>Distributed systems,<br />one failure at a time.</h1>
                    <p>Explore four Java implementations—from safe retries to replicated consensus, shard movement, and cross-group transactions.</p>
                </div>
                <div className="paxos-hero-meta">
                    <div><span>Labs</span><strong>04</strong></div>
                    <div><span>Language</span><strong>Java</strong></div>
                    <div><span>Framework</span><strong>DSLabs</strong></div>
                </div>
            </header>

            {isCoursework && (
                <div className="sim-mode-tabs" role="tablist" aria-label="Distributed systems modes">
                    <span className="sim-mode-kicker" aria-hidden="true">Mode</span>
                    {MODES.map((m) => (
                        <button
                            key={m.id}
                            role="tab"
                            aria-selected={mode === m.id}
                            className={mode === m.id ? 'is-active' : ''}
                            onClick={() => selectMode(m.id)}
                        >
                            {m.label}
                        </button>
                    ))}
                </div>
            )}

            <main className="paxos-main">
                {(!isCoursework || mode === 'guided') && (
                    <>
                        <LabSelector selectedLab={selectedLab} onSelect={setSelectedLab} />
                        {selectedLab === 3
                            ? <PaxosExperience lab={selectedAnalysis} onTryInSandbox={isCoursework ? tryGuidedInSandbox : undefined} />
                            : <LabAnalysis key={selectedLab} lab={selectedAnalysis} onTryInSandbox={isCoursework ? tryGuidedInSandbox : undefined} />}
                    </>
                )}
                {isCoursework && mode === 'sandbox' && (
                    <SimulatorErrorBoundary label="Sandbox">
                        <Suspense fallback={<p className="sim-loading" aria-busy="true">Loading sandbox…</p>}>
                            <SandboxView
                                key={sandboxLoad ? sandboxLoad.labId + sandboxLoad.nonce : 'sandbox'}
                                initialLabId={sandboxLoad ? sandboxLoad.labId : 'lab3'}
                                initialSeed={sandboxLoad ? sandboxLoad.seed : 1}
                                initialDecisions={sandboxLoad ? sandboxLoad.decisions : null}
                            />
                        </Suspense>
                    </SimulatorErrorBoundary>
                )}
                {isCoursework && mode === 'search' && (
                    <SimulatorErrorBoundary label="Search">
                        <Suspense fallback={<p className="sim-loading" aria-busy="true">Loading search…</p>}>
                            <SearchView onLoadPath={loadPathInSandbox} />
                        </Suspense>
                    </SimulatorErrorBoundary>
                )}
            </main>

            <footer className="paxos-footer">
                <span>Private coursework · Solution code is not distributed</span>
                <div>
                    <a href={protocolAuditUrl} target="_blank" rel="noreferrer">Protocol audit ↗</a>
                    <a href="https://github.com/emichael/dslabs" target="_blank" rel="noreferrer">Public DSLabs framework ↗</a>
                    <a href="mailto:adityapatel1204+wrk@gmail.com">Need the code? Contact me →</a>
                    <a href="/">Back to portfolio →</a>
                </div>
            </footer>
        </div>
    );
}
